import React, { useState, useMemo, useRef, useEffect } from 'react'
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  useMediaQuery,
  Grid
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useNavigate, useSearchParams } from 'react-router-dom'
import MenuIcon from '@mui/icons-material/Menu'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import EditIcon from '@mui/icons-material/Edit'
import QuizIcon from '@mui/icons-material/Quiz'
import LogoutIcon from '@mui/icons-material/Logout'
import AppIcon from './AppIcon'
import NotificationsIcon from './NotificationsIcon'
import { TextHover, TextSemiBold } from './TextStyling'
import { logoutUser } from '../actions/loginActions'
import { useDispatch } from 'react-redux'
import { getUserDetails } from '../utils/LocalStorage'
import { paddingHorizontal } from '../utils/AppConstants'
import { ContactSupport } from '@mui/icons-material'
import DoorBackOutlinedIcon from '@mui/icons-material/DoorBackOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined'
import CurrencyRupeeOutlinedIcon from '@mui/icons-material/CurrencyRupeeOutlined'
import PersonIcon from '@mui/icons-material/Person'

export default function HeaderEmployer() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const user = getUserDetails()
  const [value, setValue] = useState(0)
  const [anchorEl, setAnchorEl] = useState(null)


  const [walkthroughActive, setWalkthroughActive] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [highlightPosition, setHighlightPosition] = useState({})
  const [tooltipPosition, setTooltipPosition] = useState({})
  const walkthroughRefs = useRef({})


  useEffect(() => {
    if (searchParams.get('showWalkthrough') === 'true') {

      setTimeout(() => {
        setWalkthroughActive(true)
      }, 1000)


      searchParams.delete('showWalkthrough')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])


  const handleJobsMenuOpen = event => {
    setAnchorEl(event.currentTarget)
  }


  const handleJobsMenuClose = () => {
    setAnchorEl(null)
  }


  const handleJobsMenuItemClick = nav => {
    navigate(nav)
    handleJobsMenuClose()
  }

  const [headerItems, setHeaderItems] = useState(() => {
    const base = [{ label: 'Dashboard', nav: '/employer/dashboard' }]
    if (user.isSubEmployer) {
      base.push(
        { label: 'Manage jobs', nav: '/employer/managejob' },
        { label: 'Post a new job', nav: '/employer/postnewjob' }
      )
    } else {
      base.push(
        { label: 'Manage users', nav: '/employer/manageusers' },
        {
          label: 'Jobs',
          menu: [
            { label: 'Manage jobs', nav: '/employer/managejob' },
            { label: 'Post a new job', nav: '/employer/postnewjob' }
          ]
        },
        { label: 'Transactions', nav: '/employer/transactions' }
      )
    }
    return base
  })

  const walkthroughSteps = useMemo(() => {
    const baseSteps = [
      {
        target: 'dashboard',
        title: 'Dashboard',
        description: 'Your main dashboard with key metrics and overview.'
      }
    ]

    if (user.isSubEmployer) {
      baseSteps.push(
        {
          target: 'manage-jobs',
          title: 'Manage Jobs',
          description: 'View and manage all your job postings with advanced analytics.'
        },
        {
          target: 'post-a-new-job',
          title: 'Post a New Job',
          description: 'Create and publish new job with advanced targeting options.'
        }
      )
    } else {
      baseSteps.push(
        {
          target: 'manage-users',
          title: 'Manage Users',
          description: 'Control user access and permissions for your organization.'
        },
        {
          target: 'jobs',
          title: 'Jobs Menu',
          description: 'Access job management options from this dropdown.',
          expandMenu: false
        },
        {
          target: 'transactions',
          title: 'Transactions',
          description: 'View your payment history and billing information.'
        }
      )
    }

    baseSteps.push({
      target: 'account-menu',
      title: 'Profile Menu',
      description: 'Access your profile settings and account options.',
      expandAccountMenu: true
    })

    return baseSteps
  }, [user.isSubEmployer])

  const setWalkthroughRef = (key) => (el) => {
    if (el) walkthroughRefs.current[key] = el
  }


  const completeWalkthrough = () => {
    setWalkthroughActive(false)
    setCurrentStep(0)
    if (user?.id) {
      localStorage.setItem(`walkthrough_seen_${user.id}`, 'true')
    }
  }


  const skipWalkthrough = () => {
    completeWalkthrough()
  }

  useEffect(() => {
    if (walkthroughActive && walkthroughSteps[currentStep]) {
      const step = walkthroughSteps[currentStep]
      const el = walkthroughRefs.current[step.target]

      if (el) {
        const rect = el.getBoundingClientRect()
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })

        setHighlightPosition({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          height: rect.height
        })

        setTooltipPosition({
          top: rect.bottom + window.scrollY + 10,
          left: rect.left + window.scrollX
        })

        if (step.target === 'account-menu') {
          setTooltipPosition({
            top: '80px',
            left: '80%',
            transform: 'translateX(-50%)'
          })
        }

        if (step.expandMenu && step.target === 'jobs') {
          handleJobsOpen({ currentTarget: walkthroughRefs.current[step.target] })
        }
      }
    }
  }, [walkthroughActive, currentStep])

  useEffect(() => {

    const walkthroughShownThisSession = sessionStorage.getItem('walkthrough_shown');
    const hasSeenWalkthrough = localStorage.getItem('recruiter_walkthrough_completed');

    if (!hasSeenWalkthrough && !walkthroughShownThisSession) {
      setWalkthroughActive(true);

      sessionStorage.setItem('walkthrough_shown', 'true');
    }
  }, []);

  const handleWalkthroughComplete = () => {
    setWalkthroughActive(false);
    localStorage.setItem('recruiter_walkthrough_completed', 'true');
  };

  const [drawerOpen, setDrawerOpen] = useState(false)

  const [anchorProfile, setAnchorProfile] = useState(null)
  const openProfile = Boolean(anchorProfile)
  const handleProfileOpen = e => setAnchorProfile(e.currentTarget)
  const handleProfileClose = () => setAnchorProfile(null)
  const onLogout = () => {
    handleProfileClose()
    dispatch(logoutUser(navigate, true))
  }

  const [anchorJobs, setAnchorJobs] = useState(null)
  const openJobs = Boolean(anchorJobs)
  const handleJobsOpen = e => setAnchorJobs(e.currentTarget)
  const handleJobsClose = () => setAnchorJobs(null)

  const ProfilePicture = () =>
    user.profilePicture ? <Avatar src={user.profilePicture} /> : <Avatar />

  return (
    <>
      <AppBar
        position='static'
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.border.main}`
        }}
      >
        <Toolbar
          sx={{
            px: paddingHorizontal,
            height: 55,
            p: '0 0',
            ml: '10px',
            mr: '10px'
          }}
        >
          <Box
            onClick={() => navigate('/employer/dashboard')}
            sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <AppIcon sx={{ width: 100 }} />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {!isMobile &&
              headerItems.map((item, idx) =>
                item.menu ? (
                  <React.Fragment
                    key={idx}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <TextSemiBold
                      ref={setWalkthroughRef('jobs')}
                      onClick={handleJobsOpen}
                      aria-controls={openJobs ? 'jobs-menu' : undefined}
                      aria-haspopup='true'
                      aria-expanded={openJobs ? 'true' : undefined}
                      sx={{
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center'
                      }}
                    >
                      <TextHover
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        {item.label}
                        <KeyboardArrowDownIcon sx={{ width: '20px' }} />
                      </TextHover>
                    </TextSemiBold>
                    <Menu
                      id='jobs-menu'
                      anchorEl={anchorJobs}
                      open={openJobs}
                      onClose={handleJobsClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      PaperProps={{
                        elevation: 0,
                        sx: {
                          mt: 1,
                          pt: 0,
                          borderTop: `4px solid ${theme.palette.primary.main}`,
                          boxShadow: '0px 4px 12px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      {item.menu.map((sub, i) => (
                        <MenuItem
                          key={i}
                          onClick={() => {
                            handleJobsClose()
                            navigate(sub.nav)
                          }}
                        >
                          {sub.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </React.Fragment>
                ) : (
                  <TextSemiBold
                    key={idx}
                    ref={setWalkthroughRef(item.label.toLowerCase().replace(/\s/g, '-'))}
                    onClick={() => navigate(item.nav)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TextHover>{item.label}</TextHover>
                  </TextSemiBold>
                )
              )}

            {!user.isSubEmployer && !isMobile && (
              <NotificationsIcon usertype={user.usertype} />
            )}

            <Box
              ref={setWalkthroughRef('account-menu')}
              onClick={handleProfileOpen}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <ProfilePicture />
              <TextSemiBold sx={{ ml: 1, fontSize: 14 }}>
                {user.firstname} {user.lastname}
              </TextSemiBold>
            </Box>

            <Menu
              anchorEl={anchorProfile}
              open={openProfile}
              onClose={handleProfileClose}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              sx={{ mt: 1 }}
            >
              <MenuItem disabled>
                <ListItemIcon>
                  <ProfilePicture />
                </ListItemIcon>
                <Box sx={{ ml: 1 }}>
                  <Grid>
                    <TextSemiBold>
                      {user.firstname} {user.lastname}
                    </TextSemiBold>
                  </Grid>
                  <TextSemiBold variant='body2' color='text.secondary'>
                    {user.companyname}
                  </TextSemiBold>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleProfileClose()
                  navigate('/employer/editprofile')
                }}
              >
                <ListItemIcon>
                  <EditIcon fontSize='small' />
                </ListItemIcon>
                Edit Profile
              </MenuItem>
              {/* <MenuItem
                onClick={() => {
                  handleProfileClose()
                  navigate('https://www.virtualrecruiters.co/contact-us/')
                }}
              >
                <ListItemIcon>
                  <ContactSupport fontSize='small' />
                </ListItemIcon>
                Contact us
              </MenuItem> */}
              <MenuItem onClick={() => window.open('/faq', '_blank')}>
                <ListItemIcon>
                  <QuizIcon fontSize='small' />
                </ListItemIcon>
                FAQ
              </MenuItem>
              <MenuItem onClick={onLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize='small' />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
            <Grid sx={{ display: 'flex', alignItems: 'center' }}>
              <Grid>
                {!user.isSubEmployer && isMobile && (
                  <NotificationsIcon usertype={user.usertype} />
                )}
              </Grid>
              <Grid>
                {isMobile && (
                  <IconButton
                    edge='end'
                    onClick={() => handleJobsMenuItemClick(true)}
                  // sx={{
                  //   ml: 2
                  // }}
                  >
                  </IconButton>
                )}
                {isMobile && (
                  <BottomNavigation
                    showLabels
                    value={value}
                    // onChange={(_, newValue) => setValue(newValue)}
                    onChange={(e, newValue) => setValue(newValue)}
                    sx={{
                      position: 'fixed',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      borderTop: `1px solid ${theme.palette.divider}`,
                      backgroundColor: theme.palette.background.paper,
                      zIndex: theme.zIndex.appBar
                    }}
                  >

                    <BottomNavigationAction
                      label="Dashboard"
                      icon={<DoorBackOutlinedIcon />}
                      onClick={() => navigate('/employer/dashboard')}
                    />
                    <BottomNavigationAction
                      label="ManageUser"
                      icon={<PersonIcon />}
                      onClick={() => navigate('/employer/manageusers')}
                    />
                    <BottomNavigationAction
                      label='Job'
                      icon={<WorkOutlineOutlinedIcon />}
                      onClick={handleJobsOpen}
                      style={{ minWidth: '0px' }}
                    />
                    <Menu
                      anchorEl={anchorJobs}
                      open={Boolean(anchorJobs)}
                      onClose={handleJobsClose}
                      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                      PaperProps={{
                        elevation: 0,
                        sx: {
                          mt: -1,
                          borderBottom: `4px solid ${theme.palette.primary.main}`,
                          boxShadow: '0px 4px 12px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      {headerItems
                        .find(i => i.menu)
                        ?.menu.map((sub, i) => (
                          <MenuItem
                            key={i}
                            onClick={() => handleJobsMenuItemClick(sub.nav)}
                          >
                            {sub.label}
                          </MenuItem>
                        ))}
                    </Menu>
                    <BottomNavigationAction
                      label="Transactions"
                      icon={<CurrencyRupeeOutlinedIcon />}
                      onClick={() => navigate('/employer/transactions')}
                    />
                  </BottomNavigation>
                )}
              </Grid>
            </Grid>
          </Box>
        </Toolbar>
      </AppBar>
      {/* {!isMobile && (
      <button
        onClick={() => {
          setWalkthroughActive(true)
          setCurrentStep(0)
        }}
         style={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              backgroundColor: '#3b82f6',
              color: '#fff',
              padding: '10px 16px',
              borderRadius: '6px',
              zIndex: 2100
            }}
        // onMouseOver={(e) => {
        //   e.target.style.backgroundColor = '#2563eb'
        //   e.target.style.transform = 'translateY(-2px)'
        //   e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)'
        // }}
        // onMouseOut={(e) => {
        //   e.target.style.backgroundColor = '#3b82f6'
        //   e.target.style.transform = 'translateY(0px)'
        //   e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)'
        // }}
      >
        
        Start Tour
      </button>
            )} */}
      {/* Walkthrough Overlay */}
      {walkthroughActive && (
        <>
          {/* Overlay */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 2000
            }}
          />

          {/* Highlight Box */}
          <div
            style={{
              position: 'absolute',
              top: highlightPosition.top - 4,      
              left: highlightPosition.left - 4,   
              width: highlightPosition.width + 8,  
              height: highlightPosition.height + 8, 
              border: '2px solid #3b82f6',
              borderRadius: '6px',
              transition: 'all 0.3s ease',
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
              zIndex: 2001
            }}
          />


          {/* Tooltip */}
          <div
            style={{
              position: 'absolute',
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              width: '280px',
              // backgroundColor: 'white',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #ccc',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
              zIndex: 2002
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
              color: 'white'
            }}>
              <h4 style={{ margin: 0, fontSize: '16px' }}>
                {walkthroughSteps[currentStep]?.title}
              </h4>
              <button
                onClick={skipWalkthrough}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                ×
              </button>
            </div>
            <p style={{ margin: '0 0 16px', fontSize: '16px', color: 'white' }}>
              {walkthroughSteps[currentStep]?.description}
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '12px', color: 'white' }}>
                {currentStep + 1} of {walkthroughSteps.length}
              </span>


              <div style={{ display: 'flex', gap: '8px' }}>
                {/* Skip Button */}
                <button
                  onClick={skipWalkthrough}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #4a5568',
                    borderRadius: '4px',
                    background: 'transparent',
                    color: '#a0aec0',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#4a5568'
                    e.target.style.color = 'white'
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'transparent'
                    e.target.style.color = '#a0aec0'
                  }}
                >
                  Skip
                </button>
                {currentStep > 0 && (
                  <button
                    onClick={() => setCurrentStep(currentStep - 1)}
                    style={{
                      marginRight: 8,
                      padding: '6px 12px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Back
                  </button>
                )}
                <button
                  onClick={() => {
                    if (currentStep === walkthroughSteps.length - 1) {
                      completeWalkthrough()
                    } else {
                      setCurrentStep(currentStep + 1)
                    }
                  }}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {currentStep === walkthroughSteps.length - 1 ? 'Finish' : 'Next'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}